import {createElement, Fragment} from 'react';

const noop = () => {};

const defaultMock = noop;

export const getGenericPassword = noop;
export const setGenericPassword = noop;
export const resetGenericPassword = noop;
// react-native-keychain accessibility constants (mirrors the mock shape in
// Diathrive/Tests/Setup.js). SecureStore.js reads Keychain.ACCESSIBLE.* —
// without this export the web build warns
// "'ACCESSIBLE' is not exported from react-native-keychain".
export const ACCESSIBLE = {
  AFTER_FIRST_UNLOCK: 'AccessibleAfterFirstUnlock',
  AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY: 'AccessibleAfterFirstUnlockThisDeviceOnly',
  WHEN_UNLOCKED: 'AccessibleWhenUnlocked',
  WHEN_UNLOCKED_THIS_DEVICE_ONLY: 'AccessibleWhenUnlockedThisDeviceOnly',
};
// Hook mocks must return module-level singletons: components use these values
// in useEffect dependency arrays (e.g. HomePage's [navigation]), and a fresh
// object per render refires the effect every render — an infinite loop that
// kept the global LoadingModal permanently on-screen on web.
const NAVIGATION_MOCK = { navigate: noop, goBack: noop };
const SAFE_AREA_INSETS_MOCK = { top: 0, bottom: 0, left: 0, right: 0 };
const SALESFORCE_CHAT_MOCK = { init: noop, startChat: noop };
export const useNavigation = () => NAVIGATION_MOCK;
export const useSafeAreaInsets = () => SAFE_AREA_INSETS_MOCK;
export const SalesforceChatAPI = () => SALESFORCE_CHAT_MOCK;
export const CommonActions = { navigate: noop, goBack: noop };

export const Canvas = () => null;
export const Path = ({ children }) => children ?? null;
export const LinearGradient = () => null;
export const vec = () => ({ x: 0, y: 0 });
export const Skia = {
  Path: {
    Make: () => ({
      moveTo: noop,
      lineTo: noop,
      close: noop,
      reset: noop,
    }),
  },
};

export const PagerView = () => null;

// react-native-gesture-handler mocks
export const GestureHandlerRootView = ({ children }) => children;
export const PanGestureHandler = ({ children }) => children;
export const TapGestureHandler = ({ children }) => children;
export const LongPressGestureHandler = ({ children }) => children;
export const PinchGestureHandler = ({ children }) => children;
export const RotationGestureHandler = ({ children }) => children;
export const FlingGestureHandler = ({ children }) => children;
export const NativeViewGestureHandler = ({ children }) => children;
export const RawButton = (props) => props.children;
export const BaseButton = (props) => props.children;
export const RectButton = (props) => props.children;
export const BorderlessButton = (props) => props.children;
export const gestureHandlerRootHOC = (Component) => Component;
export const Directions = { RIGHT: 1, LEFT: 2, UP: 4, DOWN: 8 };
export const State = { UNDETERMINED: 0, FAILED: 1, BEGAN: 2, CANCELLED: 3, ACTIVE: 4, END: 5 };
export const Swipeable = ({ children }) => children;
export const DrawerLayout = ({ children }) => children;
export const Gesture = {
  Pan: () => ({ onStart: () => Gesture.Pan(), onUpdate: () => Gesture.Pan(), onEnd: () => Gesture.Pan() }),
  Tap: () => ({ onStart: () => Gesture.Tap(), onEnd: () => Gesture.Tap() }),
};
export const GestureDetector = ({ children }) => children;

// react-native-ble-plx mocks (Bluetooth not available on web)
export const BleManager = class {
  constructor() {}
  startDeviceScan = noop;
  stopDeviceScan = noop;
  connectToDevice = () => Promise.resolve({});
  destroy = noop;
  state = () => Promise.resolve('PoweredOn');
  onStateChange = () => ({ remove: noop });
};
export const BleError = class {};
export const Device = class {};
export const LogLevel = { None: 0, Verbose: 1, Debug: 2, Info: 3, Warning: 4, Error: 5 };
export const ConnectionPriority = { Balanced: 0, High: 1, LowPower: 2 };
export const ScanCallbackType = { AllMatches: 1, FirstMatch: 2, MatchLost: 3 };
export const ScanMode = { Balanced: 1, LowLatency: 2, LowPower: 0, Opportunistic: -1 };

// react-native-calendars mocks
export const Calendar = () => null;
export const CalendarList = () => null;
export const Agenda = () => null;
export const CalendarProvider = ({ children }) => children;
export const ExpandableCalendar = () => null;
export const WeekCalendar = () => null;
export const AgendaList = () => null;

// react-native-circular-progress mocks
export const AnimatedCircularProgress = () => null;
export const CircularProgress = () => null;

// react-native-confirmation-code-field mocks
export const CodeField = () => null;
export const Cursor = () => null;
export const useBlurOnFulfill = () => null;
export const useClearByFocusCell = () => [{}];

// react-native-dots-pagination mocks
export const DotsPagination = () => null;

// react-native-element-dropdown mocks
export const Dropdown = () => null;
export const MultiSelect = () => null;
export const SelectCountry = () => null;

// react-native-image-picker mocks
export const launchCamera = () => Promise.resolve({ didCancel: true });
export const launchImageLibrary = () => Promise.resolve({ didCancel: true });

// react-native-super-grid — FlatGrid must actually render: ProductCatalog and
// the Resources Favorites view render their web lists exclusively through it,
// so a null mock blanks the store catalog and favorites on web regardless of
// data. CSS grid reproduces super-grid's semantics (itemDimension = minimum
// column width, fit as many columns as possible).
export const FlatGrid = ({
  data = [],
  renderItem,
  itemDimension = 120,
  spacing = 12,
  ListEmptyComponent,
  style,
  testID,
}) => {
  if (!data.length) {
    if (!ListEmptyComponent) {
      return null;
    }
    return typeof ListEmptyComponent === 'function'
      ? createElement(ListEmptyComponent)
      : ListEmptyComponent;
  }
  const {marginHorizontal, ...restStyle} = style || {};
  return createElement(
    'div',
    {
      'data-testid': testID,
      style: {
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${itemDimension}px, 100%), 1fr))`,
        gap: spacing,
        padding: spacing,
        ...(marginHorizontal != null
          ? {marginLeft: marginHorizontal, marginRight: marginHorizontal}
          : {}),
        ...restStyle,
      },
    },
    data.map((item, index) =>
      createElement(Fragment, {key: item?.id ?? index}, renderItem({item, index})),
    ),
  );
};
export const SectionGrid = () => null;
export const SimpleGrid = () => null;

// react-native-swipe-gestures mocks
export const GestureRecognizer = ({ children }) => children;

// react-native-toast-message mocks
export const Toast = {
  show: noop,
  hide: noop,
  setRef: noop,
};
export const BaseToast = () => null;
export const ErrorToast = () => null;
export const InfoToast = () => null;
export const SuccessToast = () => null;

// @alessiocancian/react-native-actionsheet mocks
export const ActionSheet = ({ children }) => children;
export const ActionSheetCustom = ({ children }) => children;

// expo-camera mocks (web camera API would need different implementation)
export const CameraView = () => null;
export const Camera = {
  Constants: { Type: { back: 'back', front: 'front' } },
  requestCameraPermissionsAsync: () => Promise.resolve({ status: 'denied' }),
  getCameraPermissionsAsync: () => Promise.resolve({ status: 'denied' }),
};
export const useCameraPermissions = () => [null, noop];

// react-native-wheel-time-picker mocks
export const TimePicker = () => null;
export const Wheel = () => null;
export const TimeType = {
  hours12: 'hours12',
  hours24: 'hours24',
  min: 'min',
  sec: 'sec',
  ampm: 'ampm',
};

export default defaultMock;
